import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit, FaTrashAlt, FaPlus } from "react-icons/fa";

const MOU_TAG = "mou";

// =====================
// Interfaces
// =====================
interface ApiResponse {
  _id: string;
  image: string[];
  heading: string;
  description: string;
  tag: string;
}

interface MouFormModalProps {
  onClose: () => void;
  onSuccess: () => void;
  editingBanner?: ApiResponse | null;
}

// =====================
// Main Component
// =====================
export default function MouSection() {
  const [banners, setBanners] = useState<ApiResponse[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<ApiResponse | null>(null);

  const fetchBanners = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/common-layout?tag=${MOU_TAG}`
      );
      // API returns array or single object — normalise to array
      const raw = res.data.data;
      setBanners(Array.isArray(raw) ? raw : raw ? [raw] : []);
    } catch (error) {
      console.error("Error fetching MOU sections:", error);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleEdit = (banner: ApiResponse) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingBanner(null);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this MOU section?"))
      return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_LOCAL_URL}/api/common-layout/${id}`
      );
      fetchBanners();
    } catch (error) {
      console.error("Error deleting MOU section:", error);
    }
  };

  return (
    <div className="pt-8">
      {/* Section Header */}
      <div className="flex justify-between items-center">
        <h2 className="font-lato-regular">Section - 03</h2>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 bg-teal text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
        >
          <span className="inline-block">
            <Icon icon={FaPlus} />
          </span>
          Add MOU Section
        </button>
      </div>

      <h3 className="font-lato-regular py-2">MoU with the Government of India</h3>
      <hr />

      {/* Empty State */}
      {banners.length === 0 && (
        <p className="text-gray-500 mt-4 text-sm">
          No MOU sections found. Click "Add MOU Section" to create one.
        </p>
      )}

      {/* MOU Cards List */}
      <div className="flex flex-col gap-4 mt-4">
        {banners.map((banner, idx) => (
          <div
            key={banner._id}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden"
          >
            {/* Card Label */}
            <div className="bg-gray-50 px-5 py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500 font-lato-regular">
                MOU {idx + 1}
              </span>
            </div>

            {/* Images */}
            <div className="flex flex-nowrap gap-8 m-4">
              {Array.isArray(banner.image) &&
                banner.image.map((img, index) => (
                  <img
                    key={index}
                    src={`${process.env.REACT_APP_LOCAL_URL}/${img}`}
                    alt={`MOU ${idx + 1} image ${index + 1}`}
                    className="w-[18rem] h-[8rem] object-contain border"
                  />
                ))}
            </div>

            {/* Content */}
            <div className="p-5">
              <h2 className="text-xl font-semibold text-black mb-2 font-lato-regular">
                {banner.heading}
              </h2>
              <h6 className="text-md text-black mb-4 font-lato-regular">
                {banner.description}
              </h6>

              <div className="flex justify-start gap-3">
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                  onClick={() => handleEdit(banner)}
                >
                  <span className="inline-block mr-1">
                    <Icon icon={FaRegEdit} />
                  </span>
                  Edit
                </button>
                <button
                  className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                  onClick={() => handleDelete(banner._id)}
                >
                  <span className="inline-block mr-1">
                    <Icon icon={FaTrashAlt} />
                  </span>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <MouFormModal
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            fetchBanners();
          }}
          editingBanner={editingBanner}
        />
      )}
    </div>
  );
}

// =====================
// Modal Form Component (Create + Edit)
// =====================
const MouFormModal = ({
  onClose,
  onSuccess,
  editingBanner,
}: MouFormModalProps) => {
  const isEditing = !!editingBanner;

  const [heading, setHeading] = useState(editingBanner?.heading ?? "");
  const [description, setDescription] = useState(
    editingBanner?.description ?? ""
  );
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditing && files.length === 0) {
      alert("Please upload at least one image.");
      return;
    }

    const formData = new FormData();
    formData.append("heading", heading);
    formData.append("description", description);
    formData.append("tag", MOU_TAG);
    files.forEach((file) => formData.append("image", file));

    setSubmitting(true);
    try {
      if (isEditing) {
        await axios.patch(
          `${process.env.REACT_APP_LOCAL_URL}/api/common-layout/${editingBanner!._id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_LOCAL_URL}/api/common-layout`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving MOU section:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <h3 className="text-lg font-lato-bold mb-4">
          {isEditing ? "Edit MOU Section" : "Add New MOU Section"}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Heading */}
          <div>
            <label className="block font-lato-bold mb-1">Heading</label>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block font-lato-bold mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md p-2 h-24"
            />
          </div>

          {/* Images Upload */}
          <div>
            <label className="block font-lato-bold mb-1">
              {isEditing ? "Upload New Images (replaces existing)" : "Upload Images"}
            </label>

            {/* Existing images preview (edit mode) */}
            {isEditing && editingBanner!.image?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto mb-2">
                {editingBanner!.image.map((img, i) => (
                  <img
                    key={i}
                    src={`${process.env.REACT_APP_LOCAL_URL}/${img}`}
                    alt={`Current ${i + 1}`}
                    className="w-24 h-24 object-cover rounded-md border"
                  />
                ))}
              </div>
            )}

            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              className="w-full border border-gray-300 rounded-md p-2"
              required={!isEditing}
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? "Saving..." : isEditing ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
